import { useState, useRef } from 'react';

const API = import.meta.env.VITE_API_URL || 'https://api.ardacious.app';

const SECTOR_OPTIONS = ['Energy','LNG','Oil & Gas','Power','Carbon','Mining','Soft Commodities','Metals','Banking','Capital Markets','Sovereign Capital','Funds','Tech','Infrastructure','Government','Geopolitics','Other'];
const ROLE_OPTIONS = [
  { v: 'buyer', l: 'Buyer' }, { v: 'seller', l: 'Seller' }, { v: 'financer', l: 'Financer' },
  { v: 'logistics', l: 'Logistics' }, { v: 'licensee', l: 'Licensee' }, { v: 'sponsor', l: 'Sponsor' },
  { v: 'intro', l: 'Introduction' }, { v: 'other', l: 'Other' },
];
const TICKET_RANGES = ['Under $1M','$1M - $5M','$5M - $20M','$20M - $100M','$100M - $500M','$500M+'];

// ─── Minimal E2EE helpers (AES-256-GCM + PBKDF2-SHA-256 100k iterations) ──────
async function generateKey(): Promise<{ rawKey: Uint8Array; salt: Uint8Array }> {
  const rawKey = crypto.getRandomValues(new Uint8Array(32));
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return { rawKey, salt };
}

async function deriveKey(rawKey: Uint8Array, salt: Uint8Array): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey('raw', rawKey.buffer as ArrayBuffer, 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt.buffer as ArrayBuffer, iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function encrypt(key: CryptoKey, data: BufferSource): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
  return { ciphertext, iv };
}

function buf2b64(buf: ArrayBuffer | Uint8Array): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf instanceof ArrayBuffer ? buf : buf)));
}

// ─── ProsimosSubmit component ─────────────────────────────────────────────────
export function ProsimosSubmit() {
  const [status, setStatus] = useState<'idle'|'encrypting'|'uploading'|'submitting'|'success'|'error'>('idle');
  const [referenceId, setReferenceId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const cisRef = useRef<HTMLInputElement>(null);
  const scoRef = useRef<HTMLInputElement>(null);
  const fcoRef = useRef<HTMLInputElement>(null);
  const [cisFile, setCisFile] = useState<File | null>(null);
  const [scoFile, setScoFile] = useState<File | null>(null);
  const [fcoFile, setFcoFile] = useState<File | null>(null);

  const [form, setForm] = useState({
    title: '', description: '', sector: '', geography: '',
    productGrade: '', ticketSizeRange: '',
    rolesNeeded: [] as string[],
    submitterName: '', submitterEmail: '', submitterCompany: '',
    submitterPhone: '', sellerWebsite: '',
  });

  function set(field: string, value: string) { setForm(f => ({ ...f, [field]: value })); }
  function toggleRole(r: string) {
    setForm(f => ({ ...f, rolesNeeded: f.rolesNeeded.includes(r) ? f.rolesNeeded.filter(x => x !== r) : [...f.rolesNeeded, r] }));
  }

  const allFieldsFilled =
    form.title.trim() && form.description.trim() && form.sector &&
    form.geography.trim() && form.productGrade.trim() && form.ticketSizeRange &&
    form.rolesNeeded.length > 0 &&
    form.submitterName.trim() && form.submitterEmail.trim() &&
    form.submitterCompany.trim() && form.submitterPhone.trim() &&
    form.sellerWebsite.trim() && cisFile && scoFile && fcoFile;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!allFieldsFilled) return;
    setStatus('encrypting'); setErrorMsg('');

    try {
      // 1. Generate per-submission key
      const { rawKey, salt } = await generateKey();
      const cryptoKey = await deriveKey(rawKey, salt);

      // 2. Encrypt form data
      const formBytes = new TextEncoder().encode(JSON.stringify(form));
      const { ciphertext: formCt, iv: formIv } = await encrypt(cryptoKey, formBytes);

      // 3. Upload CIS (encrypted)
      setStatus('uploading');
      const cisBytes = await cisFile!.arrayBuffer();
      const { ciphertext: cisCt, iv: cisIv } = await encrypt(cryptoKey, cisBytes);
      const cisPayload = new Uint8Array(12 + (cisCt as ArrayBuffer).byteLength);
      cisPayload.set(cisIv, 0);
      cisPayload.set(new Uint8Array(cisCt as ArrayBuffer), 12);
      const cisBlob = new Blob([cisPayload], { type: 'application/octet-stream' });
      const cisRes = await fetch(`${API}/intake/prosimos/upload?kind=cis&filename=${encodeURIComponent(cisFile!.name)}`, {
        method: 'PUT', body: cisBlob, headers: { 'content-type': 'application/octet-stream' },
      });
      if (!cisRes.ok) throw new Error('CIS upload failed: ' + await cisRes.text());
      const { artifactId: cisArtifactId } = await cisRes.json();

      // 4. Upload SCO (encrypted)
      const scoBytes = await scoFile!.arrayBuffer();
      const { ciphertext: scoCt, iv: scoIv } = await encrypt(cryptoKey, scoBytes);
      const scoPayload = new Uint8Array(12 + (scoCt as ArrayBuffer).byteLength);
      scoPayload.set(scoIv, 0);
      scoPayload.set(new Uint8Array(scoCt as ArrayBuffer), 12);
      const scoBlob = new Blob([scoPayload], { type: 'application/octet-stream' });
      const scoRes = await fetch(`${API}/intake/prosimos/upload?kind=sco&filename=${encodeURIComponent(scoFile!.name)}`, {
        method: 'PUT', body: scoBlob, headers: { 'content-type': 'application/octet-stream' },
      });
      if (!scoRes.ok) throw new Error('SCO upload failed: ' + await scoRes.text());
      const { artifactId: scoArtifactId } = await scoRes.json();

      // 5. Upload FCO (encrypted)
      const fcoBytes = await fcoFile!.arrayBuffer();
      const { ciphertext: fcoCt, iv: fcoIv } = await encrypt(cryptoKey, fcoBytes);
      const fcoPayload = new Uint8Array(12 + (fcoCt as ArrayBuffer).byteLength);
      fcoPayload.set(fcoIv, 0);
      fcoPayload.set(new Uint8Array(fcoCt as ArrayBuffer), 12);
      const fcoBlob = new Blob([fcoPayload], { type: 'application/octet-stream' });
      const fcoRes = await fetch(`${API}/intake/prosimos/upload?kind=fco&filename=${encodeURIComponent(fcoFile!.name)}`, {
        method: 'PUT', body: fcoBlob, headers: { 'content-type': 'application/octet-stream' },
      });
      if (!fcoRes.ok) throw new Error('FCO upload failed: ' + await fcoRes.text());
      const { artifactId: fcoArtifactId } = await fcoRes.json();

      // 6. Submit encrypted form + file refs
      // wrappedKey is the raw symmetric key (server will wrap with group master key in KV)
      // In v1 this is "client-encrypts-symmetric-key" not full asymmetric E2EE —
      // the group master key is known to the server so server can always decrypt.
      setStatus('submitting');
      const submitRes = await fetch(`${API}/intake/prosimos`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          encryptedFormData: buf2b64(formCt),
          salt: buf2b64(salt),
          iv: buf2b64(formIv),
          wrappedKey: buf2b64(rawKey),  // v1: raw key, server wraps with group master key
          submitterEmail: form.submitterEmail,
          submitterName: form.submitterName,
          cisArtifactId,
          scoArtifactId,
          fcoArtifactId,
        }),
      });
      const data = await submitRes.json();
      if (submitRes.status === 429) { setErrorMsg('Rate limit exceeded — please try again later.'); setStatus('error'); return; }
      if (!submitRes.ok) { setErrorMsg(data.error ?? 'Submission failed.'); setStatus('error'); return; }
      setReferenceId(data.referenceId);
      setStatus('success');
    } catch (e: any) {
      setErrorMsg(e.message ?? 'An error occurred.');
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-6">
        <div className="max-w-lg w-full">
          <div className="hairline rounded-sm p-8 text-center">
            <div className="text-4xl mb-4">🔒</div>
            <h1 className="text-xl font-semibold mb-2">Submission received</h1>
            <p className="text-muted text-sm mb-4">Your encrypted submission has been received and is being reviewed by the Inner Circle team.</p>
            <div className="bg-whisper rounded-sm px-4 py-3 text-sm tnum font-mono text-center mb-4">{referenceId}</div>
            <p className="text-xs text-muted">Save this reference ID. You can use it to follow up with the team.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-ink">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight mb-1">Submit a Deal — Inner Circle</h1>
          <p className="text-muted text-sm">Submit a deal opportunity to the Inner Circle for consideration. All submissions are reviewed within 24 hours.</p>
        </div>

        {/* Encryption notice — prominent, above form */}
        <div className="mb-8 hairline rounded-sm p-4 bg-whisper">
          <div className="flex gap-3 items-start">
            <span className="text-xl shrink-0">🔒</span>
            <div>
              <p className="text-sm font-semibold mb-1">End-to-end encrypted</p>
              <p className="text-sm text-muted">
                AES-256-GCM with PBKDF2-SHA-256 (100,000 iterations). Your form data and files are encrypted in your browser before upload. Only Inner Circle members can decrypt them. Server never sees plaintext.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Deal details */}
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted mb-3">Deal Details</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-muted mb-1">Deal title *</label>
                <input required value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. 500,000 MT crude oil cargo, West Africa" className="w-full h-9 hairline rounded-sm px-3 text-sm bg-bg" />
              </div>
              <div>
                <label className="block text-xs text-muted mb-1">Description *</label>
                <textarea required rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Key terms, volumes, grade, origin/destination, contract structure…" className="w-full hairline rounded-sm px-3 py-2 text-sm bg-bg resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-muted mb-1">Sector *</label>
                  <select required value={form.sector} onChange={e => set('sector', e.target.value)} className="w-full h-9 hairline rounded-sm px-3 text-sm bg-bg">
                    <option value="">Select…</option>
                    {SECTOR_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Geography *</label>
                  <input required value={form.geography} onChange={e => set('geography', e.target.value)} placeholder="e.g. West Africa, Atlantic Basin" className="w-full h-9 hairline rounded-sm px-3 text-sm bg-bg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-muted mb-1">Product / grade *</label>
                  <input required value={form.productGrade} onChange={e => set('productGrade', e.target.value)} placeholder="e.g. Bonny Light, 32 API" className="w-full h-9 hairline rounded-sm px-3 text-sm bg-bg" />
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Ticket size *</label>
                  <select required value={form.ticketSizeRange} onChange={e => set('ticketSizeRange', e.target.value)} className="w-full h-9 hairline rounded-sm px-3 text-sm bg-bg">
                    <option value="">Select…</option>
                    {TICKET_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-muted mb-1">Roles sought * (select all that apply)</label>
                <div className="flex flex-wrap gap-2">
                  {ROLE_OPTIONS.map(r => {
                    const on = form.rolesNeeded.includes(r.v);
                    return (
                      <button type="button" key={r.v} onClick={() => toggleRole(r.v)}
                        className={`h-7 px-2.5 rounded-sm text-xs transition-colors hairline ${on ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink'}`}>
                        {r.l}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* Submitter info */}
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted mb-3">Submitter Information</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-muted mb-1">Full name *</label>
                  <input required value={form.submitterName} onChange={e => set('submitterName', e.target.value)} className="w-full h-9 hairline rounded-sm px-3 text-sm bg-bg" />
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Email *</label>
                  <input required type="email" value={form.submitterEmail} onChange={e => set('submitterEmail', e.target.value)} className="w-full h-9 hairline rounded-sm px-3 text-sm bg-bg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-muted mb-1">Company *</label>
                  <input required value={form.submitterCompany} onChange={e => set('submitterCompany', e.target.value)} className="w-full h-9 hairline rounded-sm px-3 text-sm bg-bg" />
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Phone *</label>
                  <input required type="tel" value={form.submitterPhone} onChange={e => set('submitterPhone', e.target.value)} className="w-full h-9 hairline rounded-sm px-3 text-sm bg-bg" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-muted mb-1">Seller / company website *</label>
                <input required type="url" value={form.sellerWebsite} onChange={e => set('sellerWebsite', e.target.value)} placeholder="https://your-company.com" className="w-full h-9 hairline rounded-sm px-3 text-sm bg-bg" />
              </div>
            </div>
          </section>

          {/* Document uploads */}
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted mb-1">Required Documents</h2>
            <p className="text-xs text-muted mb-3">All three documents are required. They will be encrypted in your browser before upload.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className={`hairline rounded-sm p-4 ${cisFile ? 'border-positive' : ''}`}>
                <p className="text-sm font-medium mb-1">CIS *</p>
                <p className="text-xs text-muted mb-2">Corporate Information Sheet</p>
                {cisFile ? (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-positive truncate max-w-[120px]">✓ {cisFile.name}</span>
                    <button type="button" onClick={() => setCisFile(null)} className="text-xs text-muted hover:text-negative">remove</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => cisRef.current?.click()} className="w-full h-8 hairline rounded-sm text-xs text-muted hover:text-ink transition-colors">
                    Upload CIS
                  </button>
                )}
                <input ref={cisRef} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden"
                  onChange={e => e.target.files?.[0] && setCisFile(e.target.files[0])} />
              </div>
              <div className={`hairline rounded-sm p-4 ${scoFile ? 'border-positive' : ''}`}>
                <p className="text-sm font-medium mb-1">SCO *</p>
                <p className="text-xs text-muted mb-2">Soft Corporate Offer</p>
                {scoFile ? (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-positive truncate max-w-[120px]">✓ {scoFile.name}</span>
                    <button type="button" onClick={() => setScoFile(null)} className="text-xs text-muted hover:text-negative">remove</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => scoRef.current?.click()} className="w-full h-8 hairline rounded-sm text-xs text-muted hover:text-ink transition-colors">
                    Upload SCO
                  </button>
                )}
                <input ref={scoRef} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden"
                  onChange={e => e.target.files?.[0] && setScoFile(e.target.files[0])} />
              </div>
              <div className={`hairline rounded-sm p-4 ${fcoFile ? 'border-positive' : ''}`}>
                <p className="text-sm font-medium mb-1">FCO *</p>
                <p className="text-xs text-muted mb-2">Full Corporate Offer</p>
                {fcoFile ? (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-positive truncate max-w-[120px]">✓ {fcoFile.name}</span>
                    <button type="button" onClick={() => setFcoFile(null)} className="text-xs text-muted hover:text-negative">remove</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => fcoRef.current?.click()} className="w-full h-8 hairline rounded-sm text-xs text-muted hover:text-ink transition-colors">
                    Upload FCO
                  </button>
                )}
                <input ref={fcoRef} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden"
                  onChange={e => e.target.files?.[0] && setFcoFile(e.target.files[0])} />
              </div>
            </div>
          </section>

          {errorMsg && (
            <div className="hairline rounded-sm p-3 text-sm text-negative bg-negative/5">{errorMsg}</div>
          )}

          <div>
            {!allFieldsFilled && (
              <p className="text-xs text-muted mb-2">
                {!cisFile || !scoFile || !fcoFile ? 'CIS, SCO, and FCO documents are all required.' : 'All fields are required before submitting.'}
              </p>
            )}
            <button
              type="submit"
              disabled={!allFieldsFilled || status !== 'idle'}
              className="w-full h-11 rounded-sm bg-ink text-bg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-ink/90"
            >
              {status === 'encrypting' ? '🔒 Encrypting…'
                : status === 'uploading' ? '📤 Uploading encrypted files…'
                : status === 'submitting' ? '📨 Submitting…'
                : '🔒 Encrypt & Submit'}
            </button>
          </div>
        </form>

        <p className="text-xs text-muted text-center mt-8">
          Powered by Inner Circle · Ardacious · Submissions reviewed within 24 hours
        </p>
      </div>
    </div>
  );
}
